import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { RefreshCw, Search, X, AlertTriangle, CheckCircle, FileText, User, Calendar, Package, ArrowUp, Info, Filter, ChevronDown } from 'lucide-react';
import MainService from '../../services/MainService';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';
import { formMultiSelectTriggerClass } from '../../utils/formControls';
import SortFilterTable, { useClientListGrid } from '../ui/SortFilterTable';
import { GridText, GridClamp, StatusPill, SELECTED_ROW_CLASS } from '../ui/gridCell';
import {
  ALLOCATION_TABLE_PAGE_SIZE,
  pickupPendingColumns,
  getPickupOrderCellValue,
} from '../ui/tableColumnConfigs';

const PushPickup = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Selected delivery note
  const [selectedDeliveryNote, setSelectedDeliveryNote] = useState(null);
  const [deliveryNoteDetails, setDeliveryNoteDetails] = useState([]);

  // Available orders (for pushing)
  const [availableOrders, setAvailableOrders] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [pageSize] = useState(ALLOCATION_TABLE_PAGE_SIZE);
  const [voucherSearchTerm, setVoucherSearchTerm] = useState('');
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);

  // Same filters as Pickup Allocation → Allocate Orders (OrdersInPickupNoteV2)
  const [filters, setFilters] = useState({
    dateFrom: new Date().toISOString().split('T')[0],
    dateTo: new Date().toISOString().split('T')[0],
    upToCurrentDate: false,
    selectedAreas: []
  });
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [areas, setAreas] = useState([]);
  const [showAreaDropdown, setShowAreaDropdown] = useState(false);
  const areaDropdownRef = useRef(null);

  // Modal states
  const [showDeliveryNoteModal, setShowDeliveryNoteModal] = useState(false);
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [modalSearchKeyword, setModalSearchKeyword] = useState('');

  // ==================== Effects ====================
  useEffect(() => {
    window.scrollTo(0, 0);
    loadAreas();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (areaDropdownRef.current && !areaDropdownRef.current.contains(event.target)) {
        setShowAreaDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // ==================== Helper Functions ====================
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const formatDateToString = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getAddressString = (address, type = 'client') => {
    if (!address || !address.Location) return '-';
    const parts = [
      address.Location.Name,
      address.Location.Area?.Name,
      address.Emirate?.Name
    ].filter(Boolean);
    return parts.join(', ') || '-';
  };

  // ==================== API Functions ====================
  const loadAreas = async () => {
    try {
      const response = await MainService.AreasInDropDown();
      setAreas(response || []);
    } catch (err) {
      console.error('Error loading areas:', err);
    }
  };

  const loadDeliveryNotes = async () => {
    try {
      setLoadingNotes(true);
      const response = await MainService.PickupNotesInList({ Skip: 0, Take: 100, Status: 'CONFIRMED' });
      if (response && Array.isArray(response)) {
        setDeliveryNotes(response);
      } else if (response && response.Data) {
        setDeliveryNotes(response.Data);
      }
    } catch (err) {
      console.error('Error loading pickup notes:', err);
      setError('Failed to load pickup notes');
    } finally {
      setLoadingNotes(false);
    }
  };

  const loadAvailableOrders = async (applyFilters = false, skip = 0, append = false, totalCount = null) => {
    try {
      if (skip === 0) {
        setLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      const useFilters = applyFilters || filtersApplied;
      const requestData = {
        isFilter: useFilters,
        Areas: useFilters && filters.selectedAreas.length > 0 ? filters.selectedAreas : null,
        FromDateString: useFilters ? formatDateToString(filters.dateFrom) : null,
        ToDateString: useFilters
          ? (filters.upToCurrentDate ? formatDateToString(new Date()) : formatDateToString(filters.dateTo))
          : null,
        IsCurrentDate: useFilters ? filters.upToCurrentDate : false,
        Skip: skip,
        Take: pageSize,
      };

      const response = await MainService.OrdersInPickupNoteV2(requestData);

      if (response) {
        const total = totalCount !== null ? totalCount : (response.Total || 0);
        const newData = response.Data || [];

        if (skip === 0) {
          setTotalOrders(total);
          setAvailableOrders(newData);
          setSelectedOrderIds([]);

          const loadedCount = newData.length;
          if (loadedCount > 0 && loadedCount === pageSize && skip + pageSize < total) {
            setTimeout(() => {
              loadAvailableOrders(useFilters, pageSize, true, total);
            }, 100);
          }
        } else {
          setAvailableOrders((prev) => {
            const existingIds = new Set(prev.map((order) => order.Id));
            const uniqueNewData = newData.filter((order) => !existingIds.has(order.Id));
            const updated = [...prev, ...uniqueNewData];
            const loadedCount = updated.length;

            const nextSkip = skip + pageSize;
            if (loadedCount < total && newData.length === pageSize && uniqueNewData.length > 0 && nextSkip < total) {
              setTimeout(() => {
                loadAvailableOrders(useFilters, nextSkip, true, total);
              }, 100);
            } else if (loadedCount >= total && loadedCount > total) {
              setTotalOrders(loadedCount);
            }

            return updated;
          });
        }
      } else if (skip === 0) {
        setAvailableOrders([]);
        setTotalOrders(0);
      }
    } catch (err) {
      console.error('Error loading available orders:', err);
      if (skip === 0) {
        setError('Failed to load orders');
        setAvailableOrders([]);
        setTotalOrders(0);
      }
    } finally {
      if (skip === 0) {
        setLoading(false);
      } else {
        setIsLoadingMore(false);
      }
    }
  };

  const loadDeliveryNoteDetails = async (noteId) => {
    try {
      const response = await MainService.GetPickupDetailsListById(noteId);
      if (response && Array.isArray(response)) {
        setDeliveryNoteDetails(response);
      } else if (response && response.Data) {
        setDeliveryNoteDetails(response.Data);
      } else {
        setDeliveryNoteDetails([]);
      }
    } catch (err) {
      console.error('Error loading pickup note details:', err);
      setDeliveryNoteDetails([]);
    }
  };

  // ==================== Event Handlers ====================
  const openDeliveryNoteModal = () => {
    setModalSearchKeyword('');
    loadDeliveryNotes();
    setShowDeliveryNoteModal(true);
  };

  const selectDeliveryNote = async (note) => {
    try {
      setLoading(true);
      setSelectedDeliveryNote(note);
      setFiltersApplied(false);
      setVoucherSearchTerm('');
      setSelectedOrderIds([]);
      await loadDeliveryNoteDetails(note.Id);
      await loadAvailableOrders(false, 0, false, null);
      setShowDeliveryNoteModal(false);
    } catch (err) {
      console.error('Error selecting pickup note:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  const pushSelectedOrders = async () => {
    if (!selectedDeliveryNote?.Id) {
      setError('Select Pickup Note');
      return;
    }
    if (selectedOrderIds.length === 0) {
      setError('Please select at least one order to push');
      return;
    }

    try {
      setLoading(true);
      const selectedOrders = availableOrders.filter((order) => selectedOrderIds.includes(order.Id));
      if (selectedOrders.length === 0) {
        setError('Please select orders to push');
        return;
      }

      let successCount = 0;
      const failures = [];

      for (const order of selectedOrders) {
        try {
          const response = await MainService.PushPickupOrder({
            OrderId: order.Id,
            PickUpNoteId: selectedDeliveryNote.Id,
          });
          if (response === 'Success' || (response && response.Status === 'Success')) {
            successCount += 1;
          } else {
            failures.push(order.VoucherNo || order.Id);
          }
        } catch (err) {
          console.error('Error pushing order:', order.Id, err);
          failures.push(order.VoucherNo || order.Id);
        }
      }

      if (successCount > 0) {
        setSuccess(
          failures.length > 0
            ? `${successCount} order(s) pushed; ${failures.length} failed`
            : `${successCount} order(s) pushed successfully`
        );
        setSelectedOrderIds([]);
        await loadDeliveryNoteDetails(selectedDeliveryNote.Id);
        await loadAvailableOrders(filtersApplied, 0, false, null);
      } else {
        setError('Failed to push selected orders');
      }
      if (failures.length > 0 && successCount === 0) {
        setError(`Failed: ${failures.slice(0, 5).join(', ')}${failures.length > 5 ? '…' : ''}`);
      }
    } catch (err) {
      console.error('Error pushing orders:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrderIds((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedOrderIds.length === availableOrders.length && availableOrders.length > 0) {
      setSelectedOrderIds([]);
    } else {
      setSelectedOrderIds(availableOrders.map((order) => order.Id));
    }
  };

  const toggleAreaSelection = (areaId) => {
    setFilters((prev) => ({
      ...prev,
      selectedAreas: prev.selectedAreas.includes(areaId)
        ? prev.selectedAreas.filter((id) => id !== areaId)
        : [...prev.selectedAreas, areaId]
    }));
  };

  const handleUpToCurrentDateChange = (checked) => {
    setFilters((prev) => ({
      ...prev,
      upToCurrentDate: checked,
      dateTo: checked ? new Date().toISOString().split('T')[0] : prev.dateTo
    }));
  };

  const handleApplyFilters = async () => {
    try {
      setLoading(true);
      setFiltersApplied(true);
      await loadAvailableOrders(true, 0, false, null);
    } catch (err) {
      console.error('Error applying filters:', err);
      setError('Failed to apply filters');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      dateFrom: new Date().toISOString().split('T')[0],
      dateTo: new Date().toISOString().split('T')[0],
      upToCurrentDate: false,
      selectedAreas: []
    });
    setVoucherSearchTerm('');
    setSelectedOrderIds([]);
    setFiltersApplied(false);
    setTotalOrders(0);
    loadAvailableOrders(false, 0, false, null);
  };

  // ==================== Filter Functions ====================
  const filteredDeliveryNotes = deliveryNotes.filter(note => {
    if (!modalSearchKeyword) return true;
    const lowerKeyword = modalSearchKeyword.toLowerCase();
    return (note.RefNo || '').toLowerCase().includes(lowerKeyword) ||
      ((note.Driver || {}).Name || '').toLowerCase().includes(lowerKeyword) ||
      (note.Status || '').toLowerCase().includes(lowerKeyword);
  });

  const pickupPendingCols = useMemo(() => pickupPendingColumns(), []);

  const getPickupCell = useCallback(
    (row, field) => getPickupOrderCellValue(row, field, formatDateToString),
    []
  );

  const renderPickupOrderCell = useCallback((row, column) => {
    const status = row.Status || {};

    if (column.field === 'status') {
      return <StatusPill status={status} />;
    }
    if (column.field === 'clientAddress' || column.field === 'remarks') {
      return <GridClamp value={getPickupOrderCellValue(row, column.field, formatDateToString)} />;
    }
    if (column.field === 'voucher') {
      return <GridText value={getPickupOrderCellValue(row, column.field, formatDateToString)} className="font-medium text-gray-900" />;
    }
    if (column.field === 'amount') {
      return <GridText value={getPickupOrderCellValue(row, column.field, formatDateToString)} className="font-medium text-gray-900" />;
    }
    return <GridText value={getPickupOrderCellValue(row, column.field, formatDateToString)} />;
  }, []);

  const availableOrderGrid = useClientListGrid({
    columns: pickupPendingCols,
    rows: availableOrders,
    getCellValue: getPickupCell,
    initialPageSize: ALLOCATION_TABLE_PAGE_SIZE,
  });

  useEffect(() => {
    if (!voucherSearchTerm || availableOrders.length === 0) return;
    const searchTerm = voucherSearchTerm.trim();
    if (!searchTerm) return;
    const matchedOrder = availableOrders.find(
      (order) => order.VoucherNo && String(order.VoucherNo).toLowerCase() === searchTerm.toLowerCase()
    );
    if (!matchedOrder) return;
    setSelectedOrderIds((prev) =>
      prev.includes(matchedOrder.Id) ? prev : [...prev, matchedOrder.Id]
    );
  }, [voucherSearchTerm, availableOrders]);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ArrowUp className="w-6 h-6 text-primary" />
              {getPageHeading(language, 'pushPickup')}
            </h1>
            <p className="text-sm text-gray-500 mt-1">{getPageHeading(language, 'pushPickupSub')}</p>
          </div>
        </div>
      </div>

      {/* Toast Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            <span>{success}</span>
          </div>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Delivery Note Selection Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {!selectedDeliveryNote ? (
          <div className="text-center py-8">
            <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Pickup Note Selected</h3>
            <p className="text-sm text-gray-500 mb-4">Select a pickup note to start pushing orders</p>
            <button
              onClick={openDeliveryNoteModal}
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex items-center gap-2 mx-auto"
            >
              <FileText className="w-5 h-5" />
              Select Pickup Note
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <FileText className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Selected Pickup Note</h3>
                  <p className="text-sm text-gray-500">Ready to push orders</p>
                </div>
              </div>
              <button
                onClick={openDeliveryNoteModal}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2 text-sm"
              >
                <RefreshCw className="w-4 h-4" />
                Change
              </button>
            </div>
            
            {/* Delivery Note Info Card */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-5 border border-blue-100">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase block">Ref No</span>
                    <span className="text-sm font-semibold text-gray-900">{selectedDeliveryNote.RefNo || '-'}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Calendar className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase block">Date</span>
                    <span className="text-sm font-semibold text-gray-900">{formatDate(selectedDeliveryNote.Date)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <User className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase block">Driver</span>
                    <span className="text-sm font-semibold text-gray-900">{(selectedDeliveryNote.Driver || {}).Name || '-'}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Package className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase block">Orders</span>
                    <span className="text-sm font-semibold text-gray-900">{deliveryNoteDetails.length}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Orders in Pickup Note */}
      {selectedDeliveryNote && deliveryNoteDetails.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 bg-white border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-600" />
                Orders in Pickup Note ({deliveryNoteDetails.length})
              </h2>
            </div>
          </div>
          <div className="overflow-x-auto" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Voucher No</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client Bill No</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client Address</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer Address</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {deliveryNoteDetails.map((detail, index) => (
                  <tr key={detail.Id || index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-600">{index + 1}</td>
                    <td className="px-4 py-3 text-gray-900 font-medium">{(detail.Order || {}).VoucherNo || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{(detail.Order || {}).ClientBillNo || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {((detail.Order || {}).Client || {}).Name || '-'}
                      {((detail.Order || {}).Client || {}).MobileNo1 && (
                        <span className="text-gray-400 ml-1">({((detail.Order || {}).Client || {}).MobileNo1})</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate" title={getAddressString((detail.Order || {}).ClientAddress, 'client')}>
                      {getAddressString((detail.Order || {}).ClientAddress, 'client')}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {((detail.Order || {}).Customer || {}).Name || '-'}
                      {((detail.Order || {}).Customer || {}).MobileNo1 && (
                        <span className="text-gray-400 ml-1">({((detail.Order || {}).Customer || {}).MobileNo1})</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate" title={getAddressString((detail.Order || {}).CustomerAddress, 'customer')}>
                      {getAddressString((detail.Order || {}).CustomerAddress, 'customer')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Filters — same as Pickup Allocation Allocate Orders */}
      {selectedDeliveryNote && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Filter className="w-5 h-5 text-primary" />
            Filters
          </h2>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Date From <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Date To <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
                  disabled={filters.upToCurrentDate}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                />
                <label className="mt-2 flex items-center gap-2 cursor-pointer text-xs font-normal text-gray-600">
                  <input
                    type="checkbox"
                    checked={filters.upToCurrentDate}
                    onChange={(e) => handleUpToCurrentDateChange(e.target.checked)}
                    className="rounded"
                  />
                  <span>Up to current date</span>
                </label>
              </div>
              <div className="md:col-span-4 relative" ref={areaDropdownRef}>
                <label className="block text-sm font-medium text-gray-700 mb-1">Areas (Multi-select)</label>
                <button
                  type="button"
                  onClick={() => setShowAreaDropdown(!showAreaDropdown)}
                  className={formMultiSelectTriggerClass}
                >
                  <span className={filters.selectedAreas.length === 0 ? 'text-gray-400' : 'text-gray-900'}>
                    {filters.selectedAreas.length === 0
                      ? 'Select Areas'
                      : `${filters.selectedAreas.length} area(s) selected`}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>

                {showAreaDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {areas.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500">No areas available</div>
                    ) : (
                      areas.map((area) => (
                        <label
                          key={area.Id}
                          className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={filters.selectedAreas.includes(area.Id)}
                            onChange={() => toggleAreaSelection(area.Id)}
                            className="rounded mr-2"
                          />
                          <span className="text-sm text-gray-700">{area.Name}</span>
                        </label>
                      ))
                    )}
                  </div>
                )}
              </div>
              <div className="md:col-span-4 self-start">
                <span className="block text-sm font-medium mb-1 invisible" aria-hidden="true">Actions</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="px-6 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm font-medium flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={handleApplyFilters}
                    disabled={loading}
                    className="px-6 py-2 bg-primary text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
                  >
                    {loading ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Loading...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Apply Filters
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Available Orders Table — same columns/multi-select as Pickup Allocate Orders */}
      {selectedDeliveryNote && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-gray-900">Available Orders</h2>
              {totalOrders > 0 && (
                <span className="text-sm text-gray-500 font-normal">
                  ({totalOrders} {totalOrders === 1 ? 'order' : 'orders'})
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search/Scan Voucher No"
                  value={voucherSearchTerm}
                  onChange={(e) => setVoucherSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                />
              </div>
              {availableOrders.length > 0 && (
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  className="text-sm text-primary hover:text-green-700 font-medium"
                >
                  {selectedOrderIds.length === availableOrders.length ? 'Deselect All' : 'Select All'}
                </button>
              )}
              <button
                type="button"
                onClick={() => loadAvailableOrders(filtersApplied, 0, false, null)}
                disabled={loading}
                className="px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 text-sm font-medium flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                type="button"
                onClick={pushSelectedOrders}
                disabled={selectedOrderIds.length === 0 || loading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm font-medium"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Pushing...</span>
                  </>
                ) : (
                  <>
                    <ArrowUp className="w-4 h-4" />
                    <span>Push {selectedOrderIds.length > 0 ? `(${selectedOrderIds.length})` : ''}</span>
                  </>
                )}
              </button>
            </div>
          </div>

          <SortFilterTable
            columns={pickupPendingCols}
            grid={availableOrderGrid}
            rows={availableOrders}
            getCellValue={getPickupCell}
            renderCell={renderPickupOrderCell}
            emptyText="No available orders"
            minWidth="1600px"
            maxHeight={560}
            showPagination
            tableClassName="w-full table-fixed"
            onRowClick={(order) => toggleOrderSelection(order.Id)}
            getRowClassName={(order) => (selectedOrderIds.includes(order.Id) ? SELECTED_ROW_CLASS : '')}
            leadingHeader={
              <th className="w-[3%] px-4 py-2 text-left">
                <input
                  type="checkbox"
                  className="rounded"
                  checked={availableOrders.length > 0 && selectedOrderIds.length === availableOrders.length}
                  onChange={toggleSelectAll}
                />
              </th>
            }
            renderLeadingCell={(order) => (
              <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  className="rounded"
                  checked={selectedOrderIds.includes(order.Id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    toggleOrderSelection(order.Id);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </td>
            )}
            footerExtra={
              isLoadingMore ? (
                <tr>
                  <td colSpan={availableOrderGrid.colSpan + 1} className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                      <span>Loading more orders...</span>
                    </div>
                  </td>
                </tr>
              ) : null
            }
          />
        </div>
      )}

      {/* Loading Overlay */}
      {loading && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg p-6 flex items-center gap-3 shadow-xl">
            <RefreshCw className="w-6 h-6 animate-spin text-primary" />
            <span className="text-gray-700 font-medium">Loading...</span>
          </div>
        </div>,
        document.body
      )}

      {/* Select Pickup Note Modal */}
      {showDeliveryNoteModal && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Select Pickup Note</h2>
                <button
                  onClick={() => setShowDeliveryNoteModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="mt-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by Ref No, Driver, or Status..."
                  value={modalSearchKeyword}
                  onChange={(e) => setModalSearchKeyword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {loadingNotes ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : filteredDeliveryNotes.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>No pickup notes found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Remarks</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created By</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Updated By</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Options</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredDeliveryNotes.map((note, index) => (
                        <tr key={note.Id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => selectDeliveryNote(note)}>
                          <td className="px-4 py-3 text-gray-600">{index + 1}</td>
                          <td className="px-4 py-3 text-gray-600">{formatDate(note.Date)}</td>
                          <td className="px-4 py-3 text-gray-900 font-medium">{note.RefNo || '-'}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              {note.Status || '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{note.Remarks || '-'}</td>
                          <td className="px-4 py-3 text-gray-600">{(note.Driver || {}).Name || '-'}</td>
                          <td className="px-4 py-3 text-gray-600">
                            {note.Vehicle ? `${note.Vehicle.Number || ''}${note.Vehicle.Model ? `, ${note.Vehicle.Model}` : ''}` : '-'}
                          </td>
                          <td className="px-4 py-3 text-gray-600">{note.CreatedName || '-'}</td>
                          <td className="px-4 py-3 text-gray-600">{note.UpdatedName || '-'}</td>
                          <td className="px-4 py-3">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                selectDeliveryNote(note);
                              }}
                              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
                            >
                              Select
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PushPickup;
